/**
 * DOL Form 5500 Import Wizard
 *
 * Multi-step wizard for importing DOL EFAST2 data to identify self-insured employers.
 *
 * Steps:
 * 1. Upload EFAST2 CSV file
 * 2. Configure filters (self-insured only, min employees, states)
 * 3. Preview detected employers with self-insured status
 * 4. Execute import
 * 5. View results
 */

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Building2,
  Loader2,
  Filter,
  Play,
  FileText,
  Shield,
  Users,
  MapPin,
  Info,
  ChevronRight,
  RefreshCw,
  Download,
  ExternalLink,
  Database,
} from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { DOLDatasetBrowser } from './DOLDatasetBrowser';
import { authFetch } from '@/lib/authFetch';

type DataSource = 'upload' | 'foia';

// Types
interface UploadResponse {
  file_id: string;
  upload_id: number;
  file_name: string;
  file_size: number;
  total_records: number;
  self_insured_count: number;
  matched_count: number;
  skipped_count: number;
  preview_data: PreviewRecord[];
  quality_summary: {
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
  };
  errors: string[];
}

interface PreviewRecord {
  company_name: string;
  ein: string;
  city: string;
  state: string;
  employee_count: number | null;
  is_self_insured: boolean;
  self_insured_confidence?: number;
  confidence?: number;
  completeness_score?: number;
  completeness?: number;
}

interface PreviewResponse {
  preview_id: string;
  file_id: string;
  total_records: number;
  self_insured_count: number;
  filtered_count: number;
  duplicate_count: number;
  preview_data: PreviewRecord[];
  quality_summary: {
    avg_confidence: number;
    avg_completeness: number;
  };
  estimated_import_count: number;
}

interface ExecutionResponse {
  job_id: number;
  status: string;
  total_records: number;
  self_insured_detected: number;
  records_created: number;
  records_updated: number;
  records_skipped: number;
  records_failed: number;
  stages: Record<string, any>;
  duration_seconds: number | null;
}

interface ImportConfig {
  targetTable: 'employers' | 'lb_company_matches';
  filterSelfInsuredOnly: boolean;
  filterMinEmployees: number | null;
  filterStates: string[];
  autoApproveThreshold: number;
  icpProfileId: string | null;
}

const STEPS = [
  { id: 'upload', label: 'Upload File', icon: Upload },
  { id: 'configure', label: 'Configure', icon: Filter },
  { id: 'preview', label: 'Preview', icon: FileText },
  { id: 'execute', label: 'Import', icon: Play },
  { id: 'results', label: 'Results', icon: CheckCircle },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

interface DOL5500ImportWizardProps {
  onComplete?: (result: ExecutionResponse) => void;
  onCancel?: () => void;
  /** Default target table: 'employers' for direct employer import, 'lb_company_matches' for lead discovery */
  targetTable?: 'employers' | 'lb_company_matches';
  /** ICP profiles available for assignment when importing to lb_company_matches */
  icpProfiles?: Array<{ id: string; name: string; profile_type?: string }>;
  /** Pre-selected ICP profile ID */
  defaultIcpProfileId?: string | null;
}

export const DOL5500ImportWizard: React.FC<DOL5500ImportWizardProps> = ({
  onComplete,
  onCancel,
  targetTable: initialTargetTable = 'employers',
  icpProfiles = [],
  defaultIcpProfileId = null,
}) => {
  const { t } = useAppTranslation();

  // Data source toggle
  const [dataSource, setDataSource] = useState<DataSource>('upload');

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [previewResponse, setPreviewResponse] = useState<PreviewResponse | null>(null);
  const [executionResult, setExecutionResult] = useState<ExecutionResponse | null>(null);

  // Loading states
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Config state - use prop as default target table
  const [config, setConfig] = useState<ImportConfig>({
    targetTable: initialTargetTable,
    filterSelfInsuredOnly: true,
    filterMinEmployees: null,
    filterStates: [],
    autoApproveThreshold: 0.8,
    icpProfileId: defaultIcpProfileId,
  });

  // Step 1: File Upload
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadedFile(file);
    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = new URL('/api/integrations/dol-5500/upload', window.location.origin);
      url.searchParams.append('target_table', config.targetTable);
      url.searchParams.append('filter_self_insured_only', String(config.filterSelfInsuredOnly));

      const response = await authFetch(url.toString(), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data: UploadResponse = await response.json();
      setUploadResponse(data);
      setActiveStep(1); // Move to configure step
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [config.targetTable, config.filterSelfInsuredOnly]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    disabled: uploading,
  });

  // Step 2: Configure & Preview
  const handlePreview = async () => {
    if (!uploadResponse) return;

    setError(null);
    setPreviewing(true);

    try {
      const url = new URL('/api/integrations/dol-5500/preview', window.location.origin);
      url.searchParams.append('file_id', uploadResponse.file_id);

      const response = await authFetch(url.toString(), {
        method: 'POST',
        jsonBody: {
          target_table: config.targetTable,
          filter_self_insured_only: config.filterSelfInsuredOnly,
          filter_min_employees: config.filterMinEmployees,
          filter_states: config.filterStates.length > 0 ? config.filterStates : null,
          auto_approve_threshold: config.autoApproveThreshold,
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Preview failed');
      }

      const data: PreviewResponse = await response.json();
      setPreviewResponse(data);
      setActiveStep(2); // Move to preview step
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  // Step 4: Execute Import
  const handleExecute = async () => {
    if (!uploadResponse) return;

    setError(null);
    setExecuting(true);

    try {
      const response = await authFetch('/api/integrations/dol-5500/execute', {
        method: 'POST',
        jsonBody: {
          file_id: uploadResponse.file_id,
          config: {
            target_table: config.targetTable,
            filter_self_insured_only: config.filterSelfInsuredOnly,
            filter_min_employees: config.filterMinEmployees,
            filter_states: config.filterStates.length > 0 ? config.filterStates : null,
            auto_approve_threshold: config.autoApproveThreshold,
            icp_profile_id: config.icpProfileId,
          },
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import failed');
      }

      const data: ExecutionResponse = await response.json();
      setExecutionResult(data);
      setActiveStep(4); // Move to results step

      if (onComplete) {
        onComplete(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setExecuting(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderConfigureStep();
      case 2:
        return renderPreviewStep();
      case 3:
        return renderExecuteStep();
      case 4:
        return renderResultsStep();
      default:
        return null;
    }
  };

  // Step 1: Upload
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Upload DOL Form 5500 Data</h3>
        <p className="text-muted-foreground">
          Upload an EFAST2 CSV file to identify self-insured employers
        </p>
      </div>

      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors',
          isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
          uploading && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-4">
          {uploading ? (
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
          ) : (
            <Upload className="h-12 w-12 text-muted-foreground" />
          )}
          <div>
            {isDragActive ? (
              <p className="text-primary font-medium">Drop the file here...</p>
            ) : (
              <>
                <p className="font-medium">Drag & drop EFAST2 CSV file here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  or click to browse (CSV or TXT files)
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Where to get DOL Form 5500 data</AlertTitle>
        <AlertDescription>
          Download EFAST2 research files from{' '}
          <a
            href="https://www.efast.dol.gov/portal/app/disseminate"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline inline-flex items-center gap-1"
          >
            DOL EFAST2 Portal <ExternalLink className="h-3 w-3" />
          </a>
        </AlertDescription>
      </Alert>

      {uploadResponse && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">{uploadResponse.file_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {uploadResponse.total_records.toLocaleString()} records
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="text-green-600 border-green-600">
                <Shield className="h-3 w-3 mr-1" />
                {uploadResponse.self_insured_count.toLocaleString()} self-insured
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Step 2: Configure
  const renderConfigureStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Configure Import</h3>
        <p className="text-muted-foreground">
          Set filters and options for the import
        </p>
      </div>

      <div className="grid gap-6">
        {/* Target Table */}
        <div className="space-y-2">
          <Label>Import Destination</Label>
          <Select
            value={config.targetTable}
            onValueChange={(value: 'employers' | 'lb_company_matches') =>
              setConfig({ ...config, targetTable: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="employers">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>Employers (Direct import for management)</span>
                </div>
              </SelectItem>
              <SelectItem value="lb_company_matches">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>Lead Discovery (For sales prospecting)</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ICP Profile Assignment (only for lead discovery target) */}
        {config.targetTable === 'lb_company_matches' && icpProfiles.length > 0 && (
          <div className="space-y-2">
            <Label>Assign to ICP Profile</Label>
            <Select
              value={config.icpProfileId || '_none'}
              onValueChange={(value) =>
                setConfig({ ...config, icpProfileId: value === '_none' ? null : value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select ICP profile (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No ICP Profile (unassigned)</SelectItem>
                {icpProfiles
                  .filter((p) => !p.profile_type || p.profile_type === 'employer')
                  .map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Link imported companies to an ICP profile for scoring and bulk contact discovery
            </p>
          </div>
        )}

        {/* Self-insured filter */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Self-Insured Only</Label>
            <p className="text-sm text-muted-foreground">
              Only import employers detected as self-insured
            </p>
          </div>
          <Switch
            checked={config.filterSelfInsuredOnly}
            onCheckedChange={(checked) =>
              setConfig({ ...config, filterSelfInsuredOnly: checked })
            }
          />
        </div>

        {/* Min employees */}
        <div className="space-y-2">
          <Label>Minimum Employees</Label>
          <Input
            type="number"
            placeholder="No minimum"
            value={config.filterMinEmployees || ''}
            onChange={(e) =>
              setConfig({
                ...config,
                filterMinEmployees: e.target.value ? parseInt(e.target.value) : null,
              })
            }
          />
          <p className="text-sm text-muted-foreground">
            Filter by minimum participant/employee count
          </p>
        </div>

        {/* State filter */}
        <div className="space-y-2">
          <Label>Filter by States</Label>
          <div className="flex flex-wrap gap-1">
            {config.filterStates.map((state) => (
              <Badge
                key={state}
                variant="secondary"
                className="cursor-pointer"
                onClick={() =>
                  setConfig({
                    ...config,
                    filterStates: config.filterStates.filter((s) => s !== state),
                  })
                }
              >
                {state} &times;
              </Badge>
            ))}
          </div>
          <Select
            value=""
            onValueChange={(value) => {
              if (value && !config.filterStates.includes(value)) {
                setConfig({
                  ...config,
                  filterStates: [...config.filterStates, value],
                });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Add state filter..." />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.filter((s) => !config.filterStates.includes(s)).map((state) => (
                <SelectItem key={state} value={state}>
                  {state}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Auto-approve threshold */}
        <div className="space-y-2">
          <Label>Auto-Approve Confidence Threshold: {(config.autoApproveThreshold * 100).toFixed(0)}%</Label>
          <Slider
            value={[config.autoApproveThreshold]}
            onValueChange={([value]) =>
              setConfig({ ...config, autoApproveThreshold: value })
            }
            min={0.5}
            max={1}
            step={0.05}
          />
          <p className="text-sm text-muted-foreground">
            Records with confidence above this threshold will be auto-approved
          </p>
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setActiveStep(0)}>
          Back
        </Button>
        <Button onClick={handlePreview} disabled={previewing}>
          {previewing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating Preview...
            </>
          ) : (
            <>
              Preview Import
              <ChevronRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Step 3: Preview
  const renderPreviewStep = () => {
    if (!previewResponse) return null;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Preview Import</h3>
          <p className="text-muted-foreground">
            Review the employers that will be imported
          </p>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{previewResponse.filtered_count.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total to Import</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {previewResponse.self_insured_count.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Self-Insured</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {previewResponse.duplicate_count.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Duplicates</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">
                {(previewResponse.quality_summary.avg_confidence * 100).toFixed(0)}%
              </p>
              <p className="text-sm text-muted-foreground">Avg Confidence</p>
            </CardContent>
          </Card>
        </div>

        {/* Preview table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Preview Data (First 100 records)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>EIN</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead>Self-Insured</TableHead>
                    <TableHead className="text-right">Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewResponse.preview_data.slice(0, 20).map((record, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{record.company_name}</TableCell>
                      <TableCell className="font-mono text-sm">{record.ein}</TableCell>
                      <TableCell>
                        {record.city && record.state ? `${record.city}, ${record.state}` : record.state || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {record.employee_count?.toLocaleString() || '-'}
                      </TableCell>
                      <TableCell>
                        {record.is_self_insured ? (
                          <Badge variant="default" className="bg-green-600">
                            <Shield className="h-3 w-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={cn(
                            (record.confidence || 0) >= 0.8
                              ? 'text-green-600 border-green-600'
                              : (record.confidence || 0) >= 0.5
                              ? 'text-amber-600 border-amber-600'
                              : 'text-red-600 border-red-600'
                          )}
                        >
                          {((record.confidence || 0) * 100).toFixed(0)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setActiveStep(1)}>
            Back
          </Button>
          <Button onClick={() => setActiveStep(3)}>
            Continue to Import
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  };

  // Step 4: Execute
  const renderExecuteStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Confirm Import</h3>
        <p className="text-muted-foreground">
          Ready to import {previewResponse?.estimated_import_count.toLocaleString()} employers
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Destination:</span>
              <span className="font-medium">
                {config.targetTable === 'employers' ? 'Employers Table' : 'Lead Discovery'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Records to import:</span>
              <span className="font-medium">
                {previewResponse?.estimated_import_count.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duplicates to skip:</span>
              <span className="font-medium text-amber-600">
                {previewResponse?.duplicate_count.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Self-insured filter:</span>
              <span className="font-medium">
                {config.filterSelfInsuredOnly ? 'Yes' : 'No'}
              </span>
            </div>
            {config.filterMinEmployees && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Min employees:</span>
                <span className="font-medium">{config.filterMinEmployees}</span>
              </div>
            )}
            {config.filterStates.length > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">States:</span>
                <span className="font-medium">{config.filterStates.join(', ')}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          The import will run a 4-stage ETL pipeline: Stage → Transform → Validate → Load.
          Records with confidence below {(config.autoApproveThreshold * 100).toFixed(0)}% may require manual review.
        </AlertDescription>
      </Alert>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setActiveStep(2)}>
          Back
        </Button>
        <Button onClick={handleExecute} disabled={executing}>
          {executing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Start Import
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Step 5: Results
  const renderResultsStep = () => {
    if (!executionResult) return null;

    const isSuccess = executionResult.status === 'completed';
    const hasErrors = executionResult.status === 'completed_with_errors';

    return (
      <div className="space-y-6">
        <div className="text-center">
          {isSuccess ? (
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          ) : hasErrors ? (
            <AlertCircle className="h-16 w-16 text-amber-600 mx-auto mb-4" />
          ) : (
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          )}
          <h3 className="text-lg font-semibold mb-2">
            {isSuccess
              ? 'Import Complete!'
              : hasErrors
              ? 'Import Completed with Errors'
              : 'Import Failed'}
          </h3>
          <p className="text-muted-foreground">
            {executionResult.records_created.toLocaleString()} employers imported successfully
          </p>
        </div>

        {/* Results summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {executionResult.records_created.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Created</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {executionResult.records_updated.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Updated</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {executionResult.records_skipped.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Skipped</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-red-600">
                {executionResult.records_failed.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Import Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Job ID:</span>
                <span className="font-mono">{executionResult.job_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant={isSuccess ? 'default' : hasErrors ? 'secondary' : 'destructive'}>
                  {executionResult.status}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Self-insured detected:</span>
                <span className="font-medium">{executionResult.self_insured_detected.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-medium">
                  {executionResult.duration_seconds
                    ? `${executionResult.duration_seconds}s`
                    : '-'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => {
            setActiveStep(0);
            setUploadedFile(null);
            setUploadResponse(null);
            setPreviewResponse(null);
            setExecutionResult(null);
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Import Another File
          </Button>
          {onCancel && (
            <Button onClick={onCancel}>
              Done
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Data source selector */}
      <div className="mb-6">
        <div className="flex items-center gap-2 p-1 bg-muted rounded-lg w-fit mx-auto">
          <button
            onClick={() => setDataSource('upload')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              dataSource === 'upload'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Upload className="h-4 w-4" />
            Upload Local File
          </button>
          <button
            onClick={() => setDataSource('foia')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              dataSource === 'foia'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Database className="h-4 w-4" />
            DOL FOIA Datasets
          </button>
        </div>
      </div>

      {/* FOIA Dataset Browser */}
      {dataSource === 'foia' ? (
        <DOLDatasetBrowser
          onImportComplete={(result) => {
            if (onComplete) {
              onComplete({
                job_id: result.job_id,
                status: result.status,
                total_records: result.total_records,
                self_insured_detected: result.self_insured_detected,
                records_created: result.records_created,
                records_updated: result.records_updated,
                records_skipped: result.records_skipped,
                records_failed: result.records_failed,
                stages: result.stages,
                duration_seconds: result.duration_seconds,
              });
            }
          }}
          onCancel={onCancel}
          targetTable={initialTargetTable}
        />
      ) : (
      <>
      {/* Step indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === activeStep;
            const isCompleted = idx < activeStep;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                      isCompleted
                        ? 'bg-primary text-primary-foreground'
                        : isActive
                        ? 'bg-primary/20 text-primary border-2 border-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xs mt-2 hidden sm:block',
                      isActive ? 'text-primary font-medium' : 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-2',
                      idx < activeStep ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step content */}
      <Card>
        <CardContent className="pt-6">
          {renderStepContent()}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default DOL5500ImportWizard;
